import React, { useState } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Share2 } from 'lucide-react';
import { useBrandTeam, BrandTeamMember } from '@/hooks/useBrandTeam';

interface TeamMemberSelectorProps {
  brandId: string;
  selectedMembers: string[];
  onSelectionChange: (memberIds: string[], roles: { [memberId: string]: string }) => void;
  disabled?: boolean;
  showShareWithAll?: boolean;
  onShareWithAll?: () => void;
}

export const TeamMemberSelector = ({ 
  brandId, 
  selectedMembers, 
  onSelectionChange,
  disabled = false,
  showShareWithAll = false,
  onShareWithAll
}: TeamMemberSelectorProps) => {
  const { teamMembers, isLoading } = useBrandTeam(brandId);
  const [memberRoles, setMemberRoles] = useState<{ [memberId: string]: string }>({});

  const handleMemberToggle = (memberId: string, checked: boolean) => {
    let newSelectedMembers: string[];
    if (checked) {
      newSelectedMembers = [...selectedMembers, memberId];
      // Set default role if not set
      if (!memberRoles[memberId]) {
        setMemberRoles(prev => ({ ...prev, [memberId]: 'viewer' }));
      }
    } else {
      newSelectedMembers = selectedMembers.filter(id => id !== memberId);
    }
    onSelectionChange(newSelectedMembers, memberRoles);
  };

  const handleSelectAll = (checked: boolean) => {
    let newSelectedMembers: string[];
    if (checked) {
      newSelectedMembers = teamMembers.map(member => member.user_id);
      // Set default roles for all members
      const newRoles = { ...memberRoles };
      teamMembers.forEach(member => {
        if (!newRoles[member.user_id]) {
          newRoles[member.user_id] = 'viewer';
        }
      });
      setMemberRoles(newRoles);
    } else {
      newSelectedMembers = [];
    }
    onSelectionChange(newSelectedMembers, memberRoles);
  };

  const handleRoleChange = (memberId: string, role: string) => {
    const newRoles = { ...memberRoles, [memberId]: role };
    setMemberRoles(newRoles);
    onSelectionChange(selectedMembers, newRoles);
  };

  const handleShareWithAllTeam = () => {
    const allMemberIds = teamMembers.map(member => member.user_id);
    const defaultRoles: { [memberId: string]: string } = {};
    allMemberIds.forEach(id => {
      defaultRoles[id] = 'viewer';
    });
    setMemberRoles(defaultRoles);
    onSelectionChange(allMemberIds, defaultRoles);
    if (onShareWithAll) {
      onShareWithAll();
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-4">
        <div className="text-sm text-muted-foreground">Loading team members...</div>
      </div>
    );
  }

  if (teamMembers.length === 0) {
    return (
      <div className="text-sm text-muted-foreground text-center py-4">
        No team members to share with
      </div>
    );
  }

  const allSelected = teamMembers.length > 0 && selectedMembers.length === teamMembers.length;
  const someSelected = selectedMembers.length > 0 && selectedMembers.length < teamMembers.length;

  return (
    <div className="space-y-3">
      {showShareWithAll && (
        <div className="flex items-center justify-between pb-2 border-b">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="select-all"
              checked={allSelected}
              onCheckedChange={handleSelectAll}
              disabled={disabled}
            />
            <Label htmlFor="select-all" className="font-medium">
              {allSelected ? 'Unselect All' : someSelected ? 'Select All' : 'Select All'}
            </Label>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleShareWithAllTeam}
            disabled={disabled}
          >
            <Share2 className="h-4 w-4 mr-2" />
            Share with All Team
          </Button>
        </div>
      )}

      {!showShareWithAll && (
        <div className="flex items-center space-x-2 pb-2 border-b">
          <Checkbox
            id="select-all"
            checked={allSelected}
            onCheckedChange={handleSelectAll}
            disabled={disabled}
          />
          <Label htmlFor="select-all" className="font-medium">
            {allSelected ? 'Unselect All' : someSelected ? 'Select All' : 'Select All'}
          </Label>
        </div>
      )}

      <div className="space-y-2 max-h-48 overflow-y-auto">
        {teamMembers.map((member) => (
          <div key={member.id} className="flex items-center space-x-3 p-2 rounded-md hover:bg-muted/50">
            <Checkbox
              id={`member-${member.user_id}`}
              checked={selectedMembers.includes(member.user_id)}
              onCheckedChange={(checked) => handleMemberToggle(member.user_id, checked as boolean)}
              disabled={disabled}
            />
            
            <Avatar className="h-8 w-8">
              <AvatarFallback className="text-xs">
                {member.profiles?.full_name
                  ?.split(' ')
                  .map(n => n[0])
                  .join('')
                  .toUpperCase() || 'U'}
              </AvatarFallback>
            </Avatar>

            <div className="flex-1 min-w-0">
              <Label 
                htmlFor={`member-${member.user_id}`}
                className="text-sm font-medium cursor-pointer"
              >
                {member.profiles?.full_name || member.profiles?.email || 'Unknown User'}
              </Label>
              {member.profiles?.email && member.profiles?.full_name && (
                <div className="text-xs text-muted-foreground truncate">
                  {member.profiles.email}
                </div>
              )}
            </div>

            {selectedMembers.includes(member.user_id) && (
              <Select
                value={memberRoles[member.user_id] || 'viewer'}
                onValueChange={(value) => handleRoleChange(member.user_id, value)}
                disabled={disabled}
              >
                <SelectTrigger className="w-24 h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="viewer">Viewer</SelectItem>
                  <SelectItem value="editor">Editor</SelectItem>
                </SelectContent>
              </Select>
            )}

            <div className="text-xs text-muted-foreground capitalize min-w-[60px]">
              {member.role}
            </div>
          </div>
        ))}
      </div>

      {selectedMembers.length > 0 && (
        <div className="text-xs text-muted-foreground pt-2 border-t">
          {selectedMembers.length} team member{selectedMembers.length !== 1 ? 's' : ''} selected
        </div>
      )}
    </div>
  );
};